import { Carousel } from '@mantine/carousel'
import { Box, Card, Stack } from '@mantine/core'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import NextSVG from '../components/buttons/next_carousel_button'
import PreviousSVG from '../components/buttons/previous_carousel_button'
import TitleDivider from '../components/divider'
import Layout from '../components/layout'
import styles from '../styles/Home.module.css'
import { searchAPI_v2 } from '../utils/axios'
import prerenderImportantLocales from '../utils/prerender_locales.js'

const platforms = [
  { id: 651, name: 'Netflix' },
  { id: 34, name: 'Amazon Prime Video' },
  { id: 433, name: 'Max' },
  { id: 266, name: 'Disney+' },
  { id: 460, name: 'Hulu' },
  { id: 54, name: 'Apple TV+' },
  { id: 725, name: 'Paramount+' },
  { id: 202, name: 'Claro Video' },
  { id: 175, name: 'CINEAR PLAY' },
  { id: 348, name: 'FilmDoo' },
  { id: 486, name: 'iQIYI' },
  { id: 1139, name: 'YouTube' },
  { id: 226, name: 'Crunchyroll' },
  { id: 620, name: 'MUBI' },
  { id: 254, name: 'DGO' },
  { id: 371, name: 'FlixOlé' },
  { id: 254, name: 'DGO' },
  { id: 264, name: 'Discovery+' },
  { id: 736, name: 'Peacock' },
  { id: 755, name: 'PlutoTV' },
  { id: 876, name: 'Crackle' },
  { id: 1090, name: 'VIX' },
  { id: 1145, name: 'YouTube TV' }
]

const removeDuplicatesBySlug = (contents) => {
  const seenSlugs = new Set()
  return contents.filter((content) => {
    if (seenSlugs.has(content.slug)) {
      return false
    }
    seenSlugs.add(content.slug)
    return true
  })
}

const fetchNewData = async (country, platformId, language) => {
  const oneWeekAgo = new Date()
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
  //yyyy-mm-dd format
  const formattedDate = oneWeekAgo.toISOString().split('T')[0]

  try {
    const response = await searchAPI_v2.get(`/content/new/${country}`, {
      params: {
        platform: platformId,
        language,
        sort_by: 'popularity',
        sort_order: 'desc',
        start: formattedDate,
        limit: 20
      }
    })

    const filteredItems = response.data?.data?.filter((item) => item.poster)
    const uniqueItems = removeDuplicatesBySlug(filteredItems)

    return { ...response.data, data: uniqueItems, platformId }
  } catch (error) {
    return { data: [], platformId }
  }
}

const New = ({ results }) => {
  const renderContent = () => {
    return platforms
      .filter((p) => {
        return results.find((r) => r.platformId === p.id)?.data?.length > 0
      })
      .map((platform) => (
        <Box
          key={platform.id}
          mt={24}
          sx={(theme) => ({
            width: '80vw',
            margin: 'auto',
            maxWidth: '1366px',
            [theme.fn.smallerThan('md')]: {
              width: '85vw'
            },
            [theme.fn.smallerThan('sm')]: {
              width: '90vw'
            }
          })}
        >
          <TitleDivider titleText={platform.name.toUpperCase()} />
          <Carousel
            h={222}
            mt={12}
            mb={20}
            slideGap="xs"
            slideSize="10.5%"
            loop={false}
            align="start"
            nextControlIcon={<NextSVG />}
            previousControlIcon={<PreviousSVG />}
            slidesToScroll={1}
            breakpoints={[
              { maxWidth: 'sm', slideSize: '30%', slideGap: 'xs' },
              { maxWidth: 'md', slideSize: '20%', slideGap: 'sm' },
              { maxWidth: 'lg', slideSize: '15%', slideGap: 'md' }
            ]}
            styles={{
              control: {
                opacity: 10,
                cursor: 'pointer',
                backgroundColor: 'transparent',
                border: 'transparent',
                transform: 'none',
                transition: 'none',
                '&[data-inactive]': {
                  opacity: 0,
                  cursor: 'default'
                }
              }
            }}
          >
            {results
              .find((r) => r.platformId === platform.id)
              ?.data?.map((mov, index) => {
                const poster = mov.translation?.poster || mov.poster
                const title = mov.translation?.title || mov.title
                // Crear una clave única combinando uid, plataforma e índice
                const uniqueKey = `${mov.uid}-${platform.id}-${index}`
                return (
                  <Carousel.Slide key={uniqueKey}>
                    <Card
                      w={140}
                      h={222}
                      p={0}
                      style={{ width: '140px', height: '222px' }}
                    >
                      <Link
                        href={`/${mov.type?.toLowerCase()}/${
                          mov.slug ? mov.slug : mov.uid
                        }`}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative'
                          }}
                        >
                          <Image
                            src={`https://images.weserv.nl/?url=${encodeURIComponent(
                              poster
                            )}&w=280&h=444s&fit=cover&output=webp`}
                            fill
                            style={{ objectFit: 'cover' }}
                            alt={title}
                          />
                        </div>
                      </Link>
                    </Card>
                  </Carousel.Slide>
                )
              })}
          </Carousel>
        </Box>
      ))
  }

  return (
    <>
      <Head>
        <title>Contents - Stream Discover</title>
        <meta name="description" content="Generated by create next app" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <main className={styles.main}>
        <Stack
          sx={(theme) => ({
            width: '80vw',
            margin: 'auto',
            maxWidth: '1366px',
            [theme.fn.smallerThan('md')]: {
              width: '85vw'
            },
            [theme.fn.smallerThan('sm')]: {
              width: '90vw'
            }
          })}
        >
          <Stack mt={20} align="center">
            {renderContent()}
          </Stack>
        </Stack>
      </main>
    </>
  )
}

New.getLayout = function getLayout(page) {
  return (
    <Layout withAutocomplete showHero pageName="NewContent">
      {page}
    </Layout>
  )
}

export default New

export async function getStaticProps({ locale }) {
  const country = locale.split('-')[1]?.toLowerCase()
  const language = locale.split('-')[0]?.toLowerCase()
  prerenderImportantLocales(locale)

  try {
    const newPromises = platforms.map((platform) =>
      fetchNewData(country, platform.id, language)
    )
    const results = await Promise.all(newPromises)
    /*     const moviesByGenre = genres.reduce((acc, genre, index) => {
      acc[genre] = results[index].data.filter((item) => item.poster)
      return acc
    }, {}) */

    const uniqueResults = results.map((result) => ({
      ...result,
      data: removeDuplicatesBySlug(result.data)
    }))

    return {
      props: {
        results: uniqueResults.filter((r) => r.data.length > 0),
        messages: (await import(`../locales/${locale.split('-')[0]}/common.json`))
          .default
      },
      revalidate: 60 * 60 * 24
    }
  } catch (error) {
    console.error('Error fetching initial data:', error)
    return {
      props: {
        results: [],
        messages: (await import(`../locales/${locale.split('-')[0]}/common.json`))
          .default
      },
      revalidate: 60 * 60 * 24
    }
  }
}
